/* AfterShip lookup. Shopify /apps/aftership cannot be iframed, so results load the AfterShip hosted tracking page in-place. */
if (!customElements.get('ks-aftership-lookup')) {
  customElements.define('ks-aftership-lookup', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const tabs = [...this.querySelectorAll('[data-mode]')];
      const panels = [...this.querySelectorAll('[data-panel]')];
      const status = this.querySelector('[data-status]');
      const result = this.querySelector('[data-result]');
      const frame = this.querySelector('[data-frame]');
      const lookup = this.querySelector('[data-lookup-panel]');
      const summary = this.querySelector('[data-summary]');
      const summaryText = this.querySelector('[data-summary-text]');
      const change = this.querySelector('[data-change]');
      const minFrameHeight = 280;
      const activate = (mode, focus = false) => {
        for (const tab of tabs) {
          const selected = tab.dataset.mode === mode;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          if (selected && focus) tab.focus();
        }
        for (const panel of panels) {
          panel.hidden = panel.dataset.panel !== mode;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabs.find(tab => tab.dataset.mode === panel.dataset.panel).id);
        }
      };
      this.querySelector('[role="tablist"]').hidden = false;
      activate('order');
      for (const [index, tab] of tabs.entries()) {
        tab.addEventListener('click', () => activate(tab.dataset.mode), options);
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          activate(tabs[next].dataset.mode, true);
        }, options);
      }

      let base;
      try {
        base = this.resolveTrackingBase();
      } catch {
        if (status) status.textContent = 'Tracking is currently unavailable. Please contact our support team below for an update.';
        return;
      }

      const showStatus = (text) => {
        if (status) status.textContent = text || '';
      };

      const isShopifyProxy = (url) => url.pathname.includes('/apps/aftership');

      const revealFrame = () => {
        window.clearTimeout(this.loadTimer);
        this.classList.remove('is-loading');
        showStatus('');
      };

      const resetLookup = () => {
        window.clearTimeout(this.loadTimer);
        this.classList.remove('is-showing-result', 'is-loading');
        if (result) result.hidden = true;
        if (summary) summary.hidden = true;
        if (lookup) lookup.hidden = false;
        if (frame) {
          frame.onload = null;
          frame.onerror = null;
          frame.removeAttribute('src');
          frame.style.height = `${minFrameHeight}px`;
        }
        showStatus('');
        lookup?.querySelector('input:not([type="hidden"])')?.focus();
      };

      window.addEventListener('message', event => {
        if (event.source !== frame?.contentWindow || !event.data) return;
        if (event.data.type === 'resize' && typeof event.data.scrollHeight === 'number') {
          frame.style.height = `${Math.max(event.data.scrollHeight, minFrameHeight)}px`;
        }
      }, options);

      change?.addEventListener('click', resetLookup, options);

      const openResult = (href, label) => {
        const dest = new URL(href, window.location.href);
        if (dest.hostname.endsWith('.aftership.com')) dest.searchParams.set('iframe', '1');
        if (isShopifyProxy(dest) || !frame || !result) {
          window.location.assign(dest.href);
          return;
        }
        this.classList.add('is-showing-result', 'is-loading');
        if (lookup) lookup.hidden = true;
        if (summary && summaryText) {
          summaryText.textContent = label;
          summary.hidden = false;
        }
        result.hidden = false;
        frame.style.height = `${minFrameHeight}px`;
        showStatus('Looking up your order…');
        frame.removeAttribute('srcdoc');
        frame.onload = () => {
          if (!/aftership\.com/i.test(frame.src)) return;
          revealFrame();
        };
        frame.onerror = revealFrame;
        window.clearTimeout(this.loadTimer);
        this.loadTimer = window.setTimeout(revealFrame, 8000);
        frame.src = dest.href;
        summary?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        change?.focus();
      };

      for (const form of this.querySelectorAll('form')) {
        form.querySelector('[type="submit"]').disabled = false;
        form.addEventListener('submit', event => {
          event.preventDefault();
          for (const input of form.querySelectorAll('input')) input.value = input.value.trim();
          if (!form.reportValidity()) return;
          const data = new FormData(form);
          const target = new URL(base);
          target.hash = '';
          for (const key of ['order-number', 'email', 'phone-number', 'tracking_number', 'iframe']) target.searchParams.delete(key);
          const prefix = target.pathname.replace(/\/$/, '').replace(/\/order-status$/, '');
          if (form.dataset.lookup === 'order') {
            target.pathname = `${prefix}/order-status`;
            target.searchParams.set('order-number', data.get('order-number'));
            target.searchParams.set('email', data.get('email'));
            openResult(target.href, `Tracking order ${data.get('order-number')}`);
          } else {
            const number = data.get('tracking-number');
            if (number === '.' || number === '..') {
              showStatus('Please check your tracking number and try again.');
              return;
            }
            target.pathname = `${prefix}/${encodeURIComponent(number)}`;
            openResult(target.href, `Tracking ${number}`);
          }
        }, options);
      }
    }

    resolveTrackingBase() {
      const raw = (this.dataset.trackingUrl || '').trim() || 'https://uv7pvfet.aftership.com/tracking';
      const url = new URL(raw, window.location.href);
      if (url.username || url.password) throw new Error('Invalid tracking URL');
      const local = url.hostname === '127.0.0.1' || url.hostname === 'localhost';
      if (url.protocol === 'http:' && !local) throw new Error('Invalid tracking URL');
      if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('Invalid tracking URL');
      return url;
    }

    disconnectedCallback() {
      window.clearTimeout(this.loadTimer);
      this.controller?.abort();
    }
  });
}
